import React, { useEffect, useState } from 'react';
import { confirmDelete } from '../../../utils/ecxSwal';
import mediaService from '../../../services/media.service';
import MediaPreviewModal, { isPdfMedia } from './subparts/MediaPreviewModal';

function formatFileSize(bytes) {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function MediaListItem({ item, updateMediaList }) {
    const [isCopied, setIsCopied] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [pdfThumbFailed, setPdfThumbFailed] = useState(false);

    useEffect(() => {
        setPdfThumbFailed(false);
    }, [item.id, item.thumbnailPath]);

    const handleDelete = async () => {
        const result = await confirmDelete({
            title: 'Delete Media?',
            itemName: item.name,
            description: 'This file will be permanently removed from your clinic media library.',
            onConfirm: () => mediaService.deleteMedia(item.clinicId, [item.id]),
        });

        if (result.isConfirmed) {
            updateMediaList();
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(item.path);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = item.path;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const fileType = item.type?.split('/').pop()?.toUpperCase() || 'FILE';
    const isImage = item.type?.startsWith('image/');
    const isPdf = isPdfMedia(item);
    const canPreview = isPdf || isImage;
    const pdfThumbnailSrc = item.thumbnailPath || null;

    const handlePreviewOpen = () => {
        if (canPreview) {
            setShowPreview(true);
        }
    };

    return (
        <>
            <div className="my-files-item ecx-page-list-item h-100 d-flex flex-column">
                <div
                    className={`my-files-item__preview${canPreview ? ' is-clickable' : ''}${isPdf ? ' my-files-item__preview--pdf' : ''}`}
                    onClick={handlePreviewOpen}
                    onKeyDown={(event) => {
                        if (canPreview && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            handlePreviewOpen();
                        }
                    }}
                    role={canPreview ? 'button' : undefined}
                    tabIndex={canPreview ? 0 : undefined}
                    title={isPdf ? 'Preview PDF' : canPreview ? 'Preview image' : undefined}
                    aria-label={canPreview ? `Preview ${item.name}` : undefined}
                >
                    {isPdf ? (
                        pdfThumbnailSrc && !pdfThumbFailed ? (
                            <img
                                src={pdfThumbnailSrc}
                                alt=""
                                loading="lazy"
                                className="my-files-item__pdf-thumb"
                                onError={() => setPdfThumbFailed(true)}
                            />
                        ) : (
                            <div className="my-files-item__preview-fallback my-files-item__preview-fallback--pdf">
                                <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>
                            </div>
                        )
                    ) : isImage ? (
                        <img src={item.path} alt={item.name} loading="lazy" />
                    ) : (
                        <div className="my-files-item__preview-fallback">
                            <i className="bi bi-file-earmark-text"></i>
                        </div>
                    )}
                    {canPreview && (
                        <span className="my-files-item__preview-overlay" aria-hidden="true">
                            <i className={`bi ${isPdf ? 'bi-eye' : 'bi-zoom-in'}`}></i>
                        </span>
                    )}
                    <span className="my-files-item__type-badge">{isPdf ? 'PDF' : fileType}</span>
                </div>

                <div className="my-files-item__body flex-grow-1 d-flex flex-column">
                    <h6 className="my-files-item__title fw-normal mb-2" title={item.name}>
                        {item.name}
                    </h6>

                    <div className="my-files-item__meta mb-2">
                        <span title={item.modifiedAt}>
                            <i className="bi bi-calendar3"></i>
                            {item.modifiedAt}
                        </span>
                        <span>
                            <i className="bi bi-hdd"></i>
                            {formatFileSize(item.sizeBytes)}
                        </span>
                    </div>

                    <div className="my-files-item__path mt-auto">
                        <div className="input-group input-group-sm">
                            <input
                                type="text"
                                className="form-control form-control-sm bg-light"
                                value={item.path}
                                readOnly
                                disabled
                                aria-label="File URL"
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleCopy}
                                disabled={isCopied}
                                title="Copy URL"
                            >
                                {isCopied ? (
                                    <i className="bi bi-check2 text-success"></i>
                                ) : (
                                    <i className="bi bi-copy"></i>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={handleDelete}
                                title="Delete file"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {canPreview && (
                <MediaPreviewModal show={showPreview} onClose={() => setShowPreview(false)} item={item} />
            )}
        </>
    );
}

export default MediaListItem;
